import { useEffect, useRef } from "react";
import {
  AreaSeries,
  CandlestickSeries,
  createChart,
  LineStyle,
  type AutoscaleInfo,
  type IChartApi,
  type IPriceLine,
  type ISeriesApi,
  type UTCTimestamp,
} from "lightweight-charts";
import type { Candle } from "../lib/api";
import { SUPPLY } from "../lib/config";

/** A horizontal level drawn on the chart, e.g. an option's strike or breakeven. Price is ETH per token. */
export type ChartLine = { priceEth: number; color: string; title: string; style?: "dashed" | "dotted" | "solid" };

type Props = {
  candles: Candle[];
  ethUsd: number;
  height?: number;
  variant?: "candles" | "area";
  lines?: ChartLine[];
};

const fmtAxis = (p: number) => (p >= 1e6 ? `$${(p / 1e6).toFixed(2)}M` : p >= 1e3 ? `$${(p / 1e3).toFixed(1)}K` : `$${p.toFixed(0)}`);
const STYLE = { dashed: LineStyle.Dashed, dotted: LineStyle.Dotted, solid: LineStyle.Solid };

/** Market cap in USD (price per token x supply x ETH/USD), as candles or an area, with optional levels on top. */
export function PriceChart({ candles, ethUsd, height = 320, variant = "candles", lines = [] }: Props) {
  const el = useRef<HTMLDivElement>(null);
  const chart = useRef<IChartApi>(undefined);
  const series = useRef<ISeriesApi<"Candlestick"> | ISeriesApi<"Area">>(undefined);
  const drawn = useRef<IPriceLine[]>([]);
  const levels = useRef<number[]>([]);

  useEffect(() => {
    if (!el.current) return;
    const c = createChart(el.current, {
      height,
      layout: {
        background: { color: "transparent" },
        textColor: "#8a8d93",
        fontFamily: "JetBrains Mono, monospace",
        fontSize: 11,
        attributionLogo: false, // credited in the footer instead
      },
      grid: { vertLines: { color: variant === "area" ? "transparent" : "#15181d" }, horzLines: { color: "#15181d" } },
      rightPriceScale: { borderColor: "#1c1f25" },
      timeScale: { borderColor: "#1c1f25", timeVisible: true, secondsVisible: false },
      crosshair: { vertLine: { color: "#3a3f48" }, horzLine: { color: "#3a3f48" } },
      localization: { priceFormatter: fmtAxis },
      handleScroll: variant === "candles",
      handleScale: variant === "candles",
    });
    // Keep every level in view, not just the price history.
    const autoscaleInfoProvider = (original: () => AutoscaleInfo | null) => {
      const r = original();
      if (!r?.priceRange || levels.current.length === 0) return r;
      return {
        ...r,
        priceRange: {
          minValue: Math.min(r.priceRange.minValue, ...levels.current),
          maxValue: Math.max(r.priceRange.maxValue, ...levels.current),
        },
      };
    };
    series.current =
      variant === "area"
        ? c.addSeries(AreaSeries, {
            lineColor: "#f2f0ea",
            lineWidth: 2,
            topColor: "rgba(242, 240, 234, 0.14)",
            bottomColor: "rgba(242, 240, 234, 0)",
            priceLineColor: "#f2f0ea",
            lastValueVisible: true,
            crosshairMarkerBorderColor: "#08090b",
            autoscaleInfoProvider,
          })
        : c.addSeries(CandlestickSeries, {
            upColor: "#c6ff3d",
            downColor: "#ff4d6d",
            borderVisible: false,
            wickUpColor: "#c6ff3d",
            wickDownColor: "#ff4d6d",
            autoscaleInfoProvider,
          });
    chart.current = c;
    const ro = new ResizeObserver(() => c.applyOptions({ width: el.current?.clientWidth ?? 600 }));
    ro.observe(el.current);
    return () => {
      ro.disconnect();
      c.remove();
      drawn.current = [];
    };
  }, [height, variant]);

  useEffect(() => {
    const k = SUPPLY * ethUsd;
    const s = series.current;
    if (!s) return;
    if (variant === "area") {
      (s as ISeriesApi<"Area">).setData(candles.map((x) => ({ time: x.t as UTCTimestamp, value: x.c * k })));
    } else {
      (s as ISeriesApi<"Candlestick">).setData(
        candles.map((x) => ({ time: x.t as UTCTimestamp, open: x.o * k, high: x.h * k, low: x.l * k, close: x.c * k })),
      );
    }
    chart.current?.timeScale().fitContent();
  }, [candles, ethUsd, variant]);

  const key = lines.map((l) => `${l.priceEth}:${l.title}:${l.color}`).join("|");
  useEffect(() => {
    const s = series.current;
    if (!s) return;
    const k = SUPPLY * ethUsd;
    for (const pl of drawn.current) s.removePriceLine(pl);
    levels.current = lines.map((l) => l.priceEth * k);
    drawn.current = lines.map((l) =>
      s.createPriceLine({
        price: l.priceEth * k,
        color: l.color,
        lineWidth: 1,
        lineStyle: STYLE[l.style ?? "dashed"],
        axisLabelVisible: true,
        title: l.title,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, ethUsd, variant]);

  return <div ref={el} style={{ width: "100%", height }} />;
}
